"use client";

import Link from "next/link";
import Image from "next/image";
import { useState, useEffect } from "react";
import { usePathname } from "next/navigation";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";

import LanguageChooserComponent from "../LanguageChooser";
import logo from "../../../public/images/logo.svg";

export interface NavLinkItem {
  href: string;
  label: string;
}

interface NavBarClientProps {
  homeHref: string;
  navLinks: NavLinkItem[];
}

const focusableSelectors = ['a[href]', 'button:not([disabled])'];

export default function NavBarClient({ homeHref, navLinks }: NavBarClientProps) {
  const pathname = usePathname();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    if (!isMenuOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsMenuOpen(false);
      }
      if (event.key === "Tab") {
        const drawer = document.getElementById("mobile-nav");
        if (!drawer) return;

        const focusable = drawer.querySelectorAll<HTMLElement>(focusableSelectors.join(","));
        if (!focusable.length) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isMenuOpen]);

  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMenuOpen]);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [pathname]);

  return (
    <div className="relative z-[1000] w-full px-4 pb-4 pt-4">
      <div className="fixed left-0 right-0 top-4 z-[1000] px-4">
        <div className="container mx-auto">
          <div className="flex items-center justify-between rounded-3xl border border-white/60 bg-white/80 p-4 shadow-xl shadow-sky-100/40 backdrop-blur-lg">
            <Link href={homeHref} onClick={() => setIsMenuOpen(false)}>
              <Image src={logo} alt="Aleksandrowicz Dental Service Logo" className="h-12 w-auto" />
            </Link>

            <div className="hidden w-full items-center justify-end gap-8 text-sm text-tremor-content-DEFAULT lg:flex">
              <nav className="flex items-center gap-6 text-base font-medium">
                {navLinks.map(link => (
                  <Link
                    key={link.href}
                    href={link.href}
                    className="rounded-full px-4 py-2 transition hover:bg-sky-50 hover:text-tremor-brand"
                  >
                    {link.label}
                  </Link>
                ))}
              </nav>
              <LanguageChooserComponent />
            </div>

            <div className="flex items-center gap-3 lg:hidden">
              <button
                type="button"
                onClick={() => setIsMenuOpen(prev => !prev)}
                className="inline-flex items-center justify-center rounded-full border border-sky-200 bg-white/80 p-2 text-sky-700 shadow-sm transition hover:border-sky-300 hover:bg-sky-50"
                aria-label={isMenuOpen ? "Close navigation" : "Open navigation"}
                aria-expanded={isMenuOpen}
              >
                {isMenuOpen ? <XMarkIcon className="h-6 w-6" /> : <Bars3Icon className="h-6 w-6" />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Spacer prevents fixed navbar from covering page content */}
      <div aria-hidden="true" className="h-28" />

      {isMenuOpen ? (
        <div
          id="mobile-nav"
          className="fixed inset-0 z-[9999] flex min-h-screen flex-col bg-white/95 backdrop-blur"
          role="dialog"
          aria-modal="true"
        >
          <div className="absolute right-6 top-6">
            <button
              type="button"
              onClick={() => setIsMenuOpen(false)}
              className="inline-flex items-center justify-center rounded-full border border-sky-200 bg-white/90 p-2 text-sky-700 shadow-sm transition hover:border-sky-300 hover:bg-sky-50"
              aria-label="Close navigation"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          <div className="mx-auto flex w-full max-w-3xl flex-1 flex-col items-center gap-10 overflow-y-auto px-6 py-24 text-center">
            <div className="flex w-full flex-col items-center gap-6">
              <div className="flex w-full items-center justify-center rounded-3xl border border-sky-100 bg-white px-4 py-4 shadow-sm">
                <LanguageChooserComponent />
              </div>
            </div>
            <nav className="flex w-full flex-col items-stretch gap-6 text-2xl font-semibold text-[var(--color-text)]">
              {navLinks.map(link => (
                <Link
                  key={`${link.href}-mobile`}
                  href={link.href}
                  onClick={() => setIsMenuOpen(false)}
                  className="w-full rounded-3xl border border-sky-100 bg-white px-6 py-4 shadow-sm transition hover:border-sky-300 hover:bg-sky-50"
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>
        </div>
      ) : null}
    </div>
  );
}
